import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle, BarChart3, Target, TrendingUp } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: "AI-Powered Mock Training",
      description: "Agents practice real customer scenarios with intelligent AI that adapts to their responses. No senior agents required—scale training instantly.",
      icon: Target,
      stats: "10x faster onboarding",
      screenshot: "/training-screenshot.jpg"
    },
    {
      title: "Automated QA Audits",
      description: "Every conversation is scored across custom parameters. Identify coaching opportunities, track improvements, and maintain quality standards automatically.",
      icon: BarChart3,
      stats: "100% coverage, zero manual work",
      screenshot: "/qa-report-screenshot.jpg"
    },
    {
      title: "RCA Insights & Coaching",
      description: "Root cause analysis reveals exactly where agents struggle. Get personalized training recommendations based on real performance data.",
      icon: TrendingUp,
      stats: "Pinpoint weak areas in seconds",
      screenshot: "/rca-screenshot.jpg"
    }
  ];


  return (
    <div className="min-h-screen bg-[#0A0F1E]">
      {/* Premium Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0F1E]/95 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/agentiq-icon.png" 
              alt="AgentIQ Logo" 
              className="w-9 h-9 object-contain group-hover:scale-110 transition-transform"
            />
            <span className="text-xl font-bold text-white">AgentIQ</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link 
              to="/login"
              className="text-sm text-white/70 hover:text-white transition-colors px-4 py-2"
            >
              Sign In
            </Link>
            <Button
              onClick={() => window.open('https://calendly.com/gagan090598', '_blank')}
              className="bg-[#2D6BFF] hover:bg-[#2D6BFF]/90 text-white px-6 py-2 rounded-lg font-semibold shadow-lg shadow-[#2D6BFF]/20"
            >
              Book a Demo
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Full Width */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden bg-[#0A0F1E]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Headline & CTA */}
            <div className="space-y-8">
              <div className="space-y-6">
                <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Smarter Agent Training,<br />
                  <span className="text-[#2D6BFF]">Better Customer</span><br />
                  <span className="text-[#2D6BFF]">Outcomes</span>
                </h1>
                <p className="text-xl text-white/70 leading-relaxed max-w-xl">
                  Stop relying on senior agents to train new hires. AI handles realistic practice, 
                  instant scoring, and personalized coaching—based on your SOPs.
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => window.open('https://calendly.com/gagan090598', '_blank')}
                  className="bg-[#2D6BFF] hover:bg-[#2D6BFF]/90 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg shadow-[#2D6BFF]/30 hover:shadow-[#2D6BFF]/50 transition-all group"
                >
                  Book a Demo
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Link 
                  to="/login"
                  className="text-white/70 hover:text-white transition-colors px-6 py-3 text-base"
                >
                  Sign In →
                </Link>
              </div>

              <div className="flex items-center gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#2D6BFF]" />
                  <span className="text-sm text-white/60">No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#2D6BFF]" />
                  <span className="text-sm text-white/60">5-min setup</span>
                </div>
              </div>
            </div>

            {/* Right: Floating Dashboard Screenshot */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 transform lg:translate-x-8 hover:scale-105 transition-transform duration-500">
                <img 
                  src="/qa-report-screenshot.jpg" 
                  alt="AgentIQ Dashboard"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E]/60 to-transparent pointer-events-none" />
              </div>
              {/* Floating accent */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#2D6BFF]/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#2D6BFF]/10 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Features - Alternating Sections */}
      {features.map((feature, idx) => (
        <section 
          key={idx}
          className={`py-24 ${idx % 2 === 0 ? 'bg-[#F7F7F5]' : 'bg-[#0A0F1E]'}`}
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
                  idx % 2 === 0 ? 'bg-[#2D6BFF]/10' : 'bg-[#2D6BFF]/20'
                }`}>
                  <feature.icon className={`w-8 h-8 ${idx % 2 === 0 ? 'text-[#2D6BFF]' : 'text-[#2D6BFF]'}`} />
                </div>
                <h2 className={`text-4xl lg:text-5xl font-serif font-bold mb-6 leading-tight ${
                  idx % 2 === 0 ? 'text-[#0A0F1E]' : 'text-white'
                }`}>
                  {feature.title}
                </h2>
                <p className={`text-lg leading-relaxed mb-6 ${
                  idx % 2 === 0 ? 'text-[#0A0F1E]/70' : 'text-white/70'
                }`}>
                  {feature.description}
                </p>
                <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
                  idx % 2 === 0 
                    ? 'bg-[#2D6BFF]/10 text-[#2D6BFF]' 
                    : 'bg-[#2D6BFF]/20 text-[#2D6BFF]'
                }`}>
                  {feature.stats}
                </div>
              </div>
              
              <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                <div className={`rounded-2xl overflow-hidden border shadow-xl ${
                  idx % 2 === 0 ? 'border-[#2D6BFF]/20' : 'border-white/10'
                }`}>
                  <img 
                    src={feature.screenshot} 
                    alt={`${feature.title} - AgentIQ Dashboard Screenshot`}
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Final CTA */}
      <section className="py-24 bg-[#0A0F1E] border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
            Ready to transform your agent training?
          </h2>
          <p className="text-xl text-white/70 mb-10">
            Join forward-thinking support teams scaling faster with AI.
          </p>
          <Button
            onClick={() => window.open('https://calendly.com/gagan090598', '_blank')}
            className="bg-[#2D6BFF] hover:bg-[#2D6BFF]/90 text-white px-10 py-6 text-lg font-semibold rounded-lg shadow-lg shadow-[#2D6BFF]/30"
          >
            Book a Free Demo
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#0A0F1E] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-white/40 text-sm">
            © 2025 AgentIQ. Built for the future of agent training.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
